import { debug } from 'console'

import type { StepId, Step } from '@epilot/journey-logic-commons/src/types/next'

import { selectStepsSorted, selectStepById } from '../selectors/config'
import { selectCurrentStep, selectLastDesiredState } from '../selectors/runtime'
import type { WritableAppState } from '../types'
import type { NavigateNextAction } from '../types/actions'

import { actionFactory } from './factory'

export const navigateNext = actionFactory<NavigateNextAction>(
  (state, { intendedStepId }) => {
    const nextStep = inferDesiredOrNextStep(state, intendedStepId)

    if (!nextStep) {
      // TODO: figure out how to handle such cases
      debug('Next Step is unreachable')

      return
    }

    const lastStepIndex = state.navigatedPath.length - 1

    // the next step is the same as the current step, therefore nothing to do
    if (nextStep.id === state.navigatedPath[lastStepIndex]) return

    // add the next step to the navigated path
    state.navigatedPath.push(nextStep.id)
  },
  'navigateNext'
)

/**
 * Infers what is the next desired/intended Step we want to transition to.
 * If a next desired Step isn't available, then we navigate to the next Step based on order index.
 */
export const inferDesiredOrNextStep = (
  state: WritableAppState,
  /** Which Step we want to transition to (comes from config) */
  intendedStepId?: StepId
): Step | undefined => {
  const desiredState = selectLastDesiredState(state)
  const nextStepIdOverride = desiredState?.nextStepOverride

  if (nextStepIdOverride) return selectStepById(state, nextStepIdOverride)

  if (intendedStepId) {
    const intendedStep = selectStepById(state, intendedStepId)

    if (intendedStep && !desiredState?.skipSteps.has(intendedStepId))
      return intendedStep
  }

  return inferSubsequentStep(state)
}

/**
 * Infers the subsequent Step we want to transition to, considering the current Step
 * and the list of skipped steps.
 */
export const inferSubsequentStep = (
  state: WritableAppState
): Step | undefined => {
  const currentStep = selectCurrentStep(state)

  // can't find the subsequent step without a starting point
  if (!currentStep) return undefined

  const sortedSteps = selectStepsSorted(state)
  const currentIndex = sortedSteps.findIndex((s) => s.id === currentStep.id)
  const skipped = selectLastDesiredState(state)?.skipSteps

  for (let i = currentIndex + 1; i < sortedSteps.length; i++) {
    const nextStep = sortedSteps[i]

    if (!skipped?.has(nextStep.id)) return nextStep
  }

  return undefined
}
